import { apiClient, ApiError } from '@/lib/api/api-client'
import { captureIdentityScope } from '@/lib/api/request-scope'
import { createLogger } from '@/lib/logger'
import { desktopAccountSession } from '@/lib/utils/native-credential-store'
import { isTauri } from '@/lib/utils/tauri'

import type { User } from '@scrambl/shared'
import { InvalidApiResponseError } from '@scrambl/shared/api-errors'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import type { CurrentUserResponse } from './types'

const logger = createLogger('modules/account-profile/api')

function isProfile(value: unknown): value is User {
  return Boolean(
    value &&
    typeof value === 'object' &&
    'id' in value &&
    typeof value.id === 'string' &&
    value.id.trim() &&
    'email' in value &&
    typeof value.email === 'string' &&
    value.email.trim(),
  )
}

/** The sole profile-read boundary for bootstrap and an established account.
 * Native legacy aliases remain subject to the vault's canonical-profile proof
 * before publication. An established browser/native profile must match exactly.
 */
export async function readCurrentAccountProfile(): Promise<CurrentUserResponse> {
  return readAccountProfile(false)
}

/** Saved selection owns refresh/recovery and must distinguish denial from outage. */
export async function readSavedDesktopAccountProfile(): Promise<CurrentUserResponse> {
  return readAccountProfile(true)
}

async function readAccountProfile(
  propagateFailure: boolean,
): Promise<CurrentUserResponse> {
  const owner = captureIdentityScope()
  const native = isTauri() ? desktopAccountSession.capture() : null
  const assertCurrent = () => {
    owner.assertCurrent()
    native?.assertCurrent()
  }
  try {
    assertCurrent()
    const response = await apiClient<CurrentUserResponse>('/auth/me', {
      method: 'GET',
      withAuth: true,
      skipOrgContext: true,
      skipDeduplication: true,
      silentAuthFailure: true,
      signal: owner.signal,
      validateResponse(value, context) {
        assertCurrent()
        if (
          !value ||
          typeof value !== 'object' ||
          !('success' in value) ||
          value.success !== true ||
          !('data' in value) ||
          !isProfile(value.data)
        )
          throw new InvalidApiResponseError(context.status)
        if (owner.profileId && value.data.id !== owner.profileId)
          throw new SessionChangedError()
      },
    })
    assertCurrent()
    return response
  } catch (error) {
    assertCurrent()
    if (error instanceof SessionChangedError) throw error
    if (propagateFailure) throw error
    if (error instanceof ApiError && [401, 403].includes(error.status))
      return { success: false, message: 'Not authenticated', data: null }
    // Neither provider errors nor arbitrary response content are diagnostics.
    logger.warn('Account profile read could not be confirmed')
    return {
      success: false,
      message: 'User profile could not be confirmed',
      data: null,
      networkError: true,
    }
  }
}
